/**
 * The edit PIN, remembered per session.
 *
 * The edit token lives in sessionStorage, which belongs to one tab: switch to another tab or app and come back
 * to a reopened page, or open the session in a new tab, and the tab is view-only again — in the middle of the
 * four-step setup — until the PIN is typed once more. So the token the server minted is also kept here, in
 * localStorage under the session it was minted for, and picked up by any tab that joins that session while it
 * is still good. It is the server's own token, so nothing is trusted that the server does not check: it has
 * its signature and a 24 h life (backend/edit_token.py), and a refused write drops it (see api/client).
 *
 * Sliding back to View is the one way to forget it on purpose.
 */

const PREFIX = 'ac_roster_edit_grant:';
/** The server's token life is 24 h; one that is about to run out is not worth restoring. */
const USABLE_MS = 23 * 3600_000;

/** When a token was minted, read off it ("session.issuedAt.signature") — null if it does not look like one. */
function issuedAt(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const seconds = Number(parts[1]);
  return Number.isFinite(seconds) ? seconds * 1000 : null;
}

export function saveEditGrant(sessionId: string | null, token: string | null): void {
  if (!sessionId || !token) return;
  try {
    localStorage.setItem(`${PREFIX}${sessionId}`, token);
  } catch {
    // Only costs the PIN again in the next tab.
  }
}

/** The remembered token for `sessionId` if it is still usable, else null (and forgotten). */
export function loadEditGrant(sessionId: string | null): string | null {
  if (!sessionId) return null;
  try {
    const token = localStorage.getItem(`${PREFIX}${sessionId}`);
    if (!token) return null;
    const minted = issuedAt(token);
    if (!token.startsWith(`${sessionId}.`) || minted === null || Date.now() - minted > USABLE_MS) {
      clearEditGrant(sessionId);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function clearEditGrant(sessionId: string | null): void {
  if (!sessionId) return;
  try {
    localStorage.removeItem(`${PREFIX}${sessionId}`);
  } catch {
    // Nothing to do — a token that cannot be removed ages out by itself.
  }
}
